import renderForRoR from "../../shared/lib/renderForRoR";

import React, { useContext, useEffect, useRef, useState } from "react";
import { newPostPath } from "../../../../routes";
import { FlipperContext } from "../../../shared/lib/FlipperContext";
import { Flash, Pagy, SharedTag } from "../../../shared/lib/types";
import Background from "../../shared/components/ui/Background";
import Select from "../../shared/components/ui/Select";

import Footer from "../../shared/components/app/Footer";
import GeneralHeader from "../../shared/components/app/GeneralHeader";
import PagyPagination from "../../shared/components/app/PagyPagination";
import PostSummaryCard from "../../shared/components/app/PostSummaryCard";
import ServiceCaution from "../../shared/components/app/ServiceCaution";
import AddIcon from "../../shared/components/icons/AddIcon";
import Application from "../../shared/components/layouts/Application";
import { SharedCurrentUser } from "../../shared/lib/types";
import SearchForm from "../components/SearchForm";
import ChangeThemeButtons from "../components/ChangeThemeButtons";
import { Posts } from "../lib/types";
import EventBanner from "../components/EventBanner";
import bannerSp from "../assets/bannerSp";

import { Show, Box, Container, Flex, Stack, Text } from "@chakra-ui/react";
import NoSearchResult from "../../shared/components/app/NoSearchResult";
import IntroduceBanner from "../../shared/components/app/IntroduceBanner";
import { BusinessMatchingBanner } from "../../shared/components/app/BusinessMatchingBanner";

export const PostsIndex = ({
  posts,
  tags,
  flash,
  pagy,
  currentUser,
}: {
  posts: Posts;
  tags: SharedTag[];
  flash: Flash;
  pagy: Pagy;
  currentUser: SharedCurrentUser;
}) => {
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setKeyword(params.get("q[title_or_content]") ?? "");
  }, []);

  const ref = useRef<HTMLDivElement>(null);
  const flipper = useContext(FlipperContext);

  // 検索条件があるかどうかを判定する関数
  const isSearchActive = () => {
    const params = new URLSearchParams(location.search);
    const hasKeyword = (params.get("q[title_or_content]") ?? "").trim() !== "";
    const hasTags = params.getAll("q[tags_id_eq_any][]").length > 0;
    const hasBookmark = params.get("only_bookmarked") === "1";
    return hasKeyword || hasTags || hasBookmark;
  };

  useEffect(() => {
    const url = new URL(location.href);
    let newDesignValue = null;
    if (currentUser != null) {
      newDesignValue = currentUser.current_post_theme_name === "compact" ? "1" : "2";
    } else {
      newDesignValue = localStorage.getItem("current_post_theme_name") === "compact" ? "1" : "2";
    }
    url.searchParams.set("new_design", newDesignValue);

    window.history.replaceState(null, "", url.toString());
  }, [currentUser, flipper]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("search") == "1" && ref.current) {
      ref.current.scrollIntoView({ block: "start" });
      window.scrollTo(0, window.scrollY - 84);
    }
  }, [flipper]);

  const [pageOrderValue, setPageOrderValue] = useState("");

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    setPageOrderValue(search.get("order") ?? "");
  }, []);

  return (
    <Application flash={flash} currentUser={currentUser}>
      {flipper.introduce_campaign_feature && <IntroduceBanner />}
      <GeneralHeader currentUser={currentUser} />
      {flipper.event_feature && (
        <Show below="lg">
          <Box w="full" as="a" href="https://aloop.jp/announcements/20250325_140806" target="_blank" rel="noreferrer">
            <Box as="img" src={bannerSp} w="full" />
          </Box>
        </Show>
      )}
      {!flipper.introduce_campaign_feature && currentUser?.is_show_business_matching_banner && (
        <BusinessMatchingBanner />
      )}
      <Background color="brand.50">
        <Container maxW="container.lg" mt={{ base: 8, sm: 14 }}>
          <Flex gap={12} direction={{ base: "column", lg: "column" }}>
            <Box flex={1}>
              <SearchForm tags={tags} />
              <Flex justifyContent={"space-between"} alignItems={"flex-end"} mt={{ base: 3, lg: 6 }}>
                {isSearchActive() && (
                  <Flex alignItems={"baseline"} gap={0} lineHeight={1}>
                    <Text fontSize={"xs"}>検索結果：</Text>
                    <Text fontSize={"md"} fontWeight={"bold"}>
                      {pagy.count}
                    </Text>
                    <Text fontSize={"xs"}>件</Text>
                  </Flex>
                )}
                <Flex gap={3} ml={isSearchActive() ? 0 : "auto"}>
                  <Select
                    value={pageOrderValue}
                    size={"sm"}
                    fontSize="xs"
                    height="auto"
                    sx={{ py: 1 }}
                    onChange={(e) => {
                      const href = new URL(location.href);
                      href.searchParams.delete("order");
                      href.searchParams.delete("page");
                      href.searchParams.append("order", e.target.value);
                      location.href = href.toString();
                    }}
                  >
                    <option value="">新着順</option>
                    <option value="comments">回答数順</option>
                    <option value="views">閲覧数順</option>
                  </Select>
                  <Box flexShrink={0} display={"flex"}>
                    <ChangeThemeButtons currentUser={currentUser} />
                  </Box>
                </Flex>
              </Flex>
              {posts.length > 0 ? (
                <Stack gap={4} ref={ref} mt={3}>
                  {posts.map((post) => (
                    <PostSummaryCard
                      post={post}
                      key={post.code}
                      // eslint-disable-next-line no-irregular-whitespace
                      query={keyword.split(/ |　/)}
                      currentUser={currentUser}
                    />
                  ))}
                </Stack>
              ) : (
                <Box mt={{ base: 3, md: 4 }}>
                  <NoSearchResult>
                    検索条件に該当する投稿は見つかりませんでした。
                    <br />
                    検索条件を変更して再検索してください。
                  </NoSearchResult>
                </Box>
              )}

              <Flex justifyContent="center" mt={14}>
                <PagyPagination pagy={pagy} />
              </Flex>
              <Box mt={12}>
                <ServiceCaution />
              </Box>
            </Box>
            {flipper.event_feature && <EventBanner />}
          </Flex>
        </Container>
        <Show below="md">
          <Box position="sticky" w="fit-content" pr={8} ml="auto" mt={2} bottom={8} mr={{ base: 0, md: 56 }}>
            <Stack align="center" w="fit-content">
              <Box
                as="a"
                href={newPostPath()}
                borderRadius="full"
                bgColor="#348875"
                w="fit-content"
                p={3}
                float="right"
              >
                <AddIcon boxSize={8} color="white" />
              </Box>
            </Stack>
          </Box>
        </Show>
      </Background>
      <Footer />
    </Application>
  );
};

export default renderForRoR(PostsIndex);
